import { useRef } from "react"

interface UseDropdownPositionOptions {
  itemCount: number // Number of items that will be displayed in the dropdown
  itemHeight?: number // Height in pixels of each dropdown item (default: 40)
  extraPadding?: number // Extra padding around the dropdown content in pixels (default: 16)
  mobileThreshold?: number // Threshold for mobile positioning logic as percentage of viewport height (default: 0.5)
  spaceBuffer?: number // Buffer space in pixels required below dropdown before staying in downward position (default: 100)
  isTransparent?: boolean // If true, uses transparent styling (absolute z-50 p-0 w-fit pointer-events-none) instead of default dropdown styles (default: false)
  customStyles?: {
    baseClasses?: string // Custom base classes to replace default styling
    upwardClasses?: string // Custom classes for upward positioning (default: 'bottom-full mb-1')
    downwardClasses?: string // Custom classes for downward positioning (default: 'top-full mt-1')
  }
}

/**
 * Custom hook for intelligent dropdown positioning with smooth animations that prevents dropdowns
 * from being cut off by viewport edges or mobile footers.
 *
 * Features:
 * - Real-time position calculation without state management
 * - Automatically detects if dropdown would be cut off by viewport bottom
 * - Special mobile footer detection to prevent overlap with fixed bottom elements
 * - Configurable thresholds for different dropdown behaviors
 * - Smooth fade-in/scale animations for open/close transitions
 * - Built-in transition classes with customizable duration and easing
 * - No async updates or timing issues - works seamlessly with Headless UI
 *
 * @param options Configuration object for positioning behavior
 * @returns Object containing buttonRef and getDropdownClassName function
 *
 * @example
 * ```tsx
 * const { buttonRef, getDropdownClassName } = useDropdownPosition({
 *   itemCount: choices.length + 1, // +1 for placeholder
 *   mobileThreshold: 0.5,
 *   spaceBuffer: 100
 * })
 *
 * <ListboxButton ref={buttonRef}>...</ListboxButton>
 * <ListboxOptions className={({ open }) => getDropdownClassName(open)}>
 *   ...
 * </ListboxOptions>
 * ```
 */
export const useDropdownPosition = (options: UseDropdownPositionOptions) => {
  const {
    itemCount,
    itemHeight = 40,
    extraPadding = 16,
    mobileThreshold = 0.5,
    spaceBuffer = 100,
    isTransparent = false,
    customStyles,
  } = options

  // Ref to the dropdown button element for position calculations
  const buttonRef = useRef<HTMLButtonElement>(null)

  /**
   * Calculates whether the dropdown should open upward or downward based on:
   * 1. Available space above and below the button
   * 2. Mobile footer detection to prevent overlap
   * 3. Viewport positioning thresholds
   *
   * @returns true if dropdown should open upward, false if downward
   */
  const checkDropdownPosition = (): boolean => {
    // Early return if button ref is not available
    if (!buttonRef.current) return false

    // Get button position relative to viewport
    const buttonRect = buttonRef.current.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const spaceBelow = viewportHeight - buttonRect.bottom
    const spaceAbove = buttonRect.top

    // Calculate how much height the dropdown will need
    const estimatedHeight = itemCount * itemHeight + extraPadding
    const maxHeight = 240 // Max height before scrolling kicks in
    const requiredHeight = Math.min(estimatedHeight, maxHeight)

    // Detect mobile devices for special positioning logic
    const isMobile = window.innerWidth < 768

    if (isMobile) {
      // Mobile-specific positioning logic to handle fixed bottom footers

      // Try to find the actual mobile footer element (checkout buttons, etc.)
      const mobileFooter = document.querySelector(
        '[class*="fixed"][class*="bottom-0"]'
      )
      let mobileFooterHeight = 200 // Fallback height if footer not found

      if (mobileFooter) {
        const footerRect = mobileFooter.getBoundingClientRect()
        mobileFooterHeight = footerRect.height || 200
      }

      // Calculate positioning conditions
      const isInLowerHalf = buttonRect.bottom > viewportHeight * mobileThreshold
      const wouldOverlapFooter =
        buttonRect.bottom + requiredHeight >
        viewportHeight - mobileFooterHeight - 20

      // Priority 1: Prevent footer overlap if there's space above
      if (wouldOverlapFooter && spaceAbove >= requiredHeight) {
        return true
      }

      // Priority 2: Flip if in lower portion and insufficient space below
      if (isInLowerHalf && spaceBelow < requiredHeight + spaceBuffer) {
        return true
      }

      // Priority 3: Aggressive check for very low buttons (bottom 40% of screen)
      if (
        buttonRect.bottom > viewportHeight * 0.6 &&
        spaceAbove >= requiredHeight
      ) {
        return true
      }
    }

    // Standard desktop logic: flip only if insufficient space below but adequate space above
    return spaceBelow < requiredHeight && spaceAbove >= requiredHeight
  }

  /**
   * Simple, efficient dropdown positioning without state management.
   * Calculates position in real-time when dropdown is open.
   *
   * @param open Whether the dropdown is currently open (from Headless UI state)
   * @returns CSS className string with proper positioning classes
   */
  const getDropdownClassName = (open: boolean): string => {
    // Only calculate position when dropdown is actually open
    const shouldDropUp = open ? checkDropdownPosition() : false

    // Determine base classes: transparent, custom, or default
    let baseClasses: string
    if (customStyles?.baseClasses) {
      baseClasses = customStyles.baseClasses
    } else if (isTransparent) {
      baseClasses =
        "absolute z-50 p-0 w-fit pointer-events-auto transition-all duration-300 ease-out"
    } else {
      baseClasses =
        "absolute z-50 w-full rounded-md bg-theme_background py-1 text-sm shadow-lg border border-input_border focus:outline-none max-h-60 overflow-auto transition-all duration-300 ease-out"
    }

    const upwardClasses = customStyles?.upwardClasses ?? "bottom-full mb-1"
    const downwardClasses = customStyles?.downwardClasses ?? "top-full mt-1"

    // Add animation states for smooth open/close transitions with directional awareness
    const animationClasses = open
      ? "opacity-100 scale-100 translate-y-0"
      : shouldDropUp
      ? "opacity-0 scale-95 translate-y-2" // Slide down when closing upward dropdown
      : "opacity-0 scale-95 -translate-y-2" // Slide up when closing downward dropdown

    // Return CSS classes with proper positioning and animations
    return `${baseClasses} ${
      shouldDropUp ? upwardClasses : downwardClasses
    } ${animationClasses}`
  }

  return {
    /** Ref to attach to the dropdown button element */
    buttonRef,
    /** Function to generate dropdown CSS classes based on open state */
    getDropdownClassName,
  }
}
